import React, { useState, useEffect } from 'react';
import * as authService from '../../services/authService';
import { Modal } from '../Common/Modal';
import { useToast } from '../Common/Toast';

const ROLES = [
  { value: 'viewer', label: 'Lecteur' },
  { value: 'editor', label: 'Éditeur' },
  { value: 'admin', label: 'Administrateur' },
];

const MAIN_ADMIN_ID = 1;

export function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const { addToast } = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await authService.getUsers();
      setUsers(data);
    } catch (err) {
      addToast('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAdd = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    setDeletingUser(user);
  };

  const handleDeleteConfirm = async (reassignTo) => {
    if (!deletingUser) return;
    try {
      await authService.deleteUser(deletingUser.id, reassignTo || null);
      addToast('Utilisateur supprimé', 'success');
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      addToast(err.response?.data?.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingUser) {
        await authService.updateUser(editingUser.id, data);
        addToast('Utilisateur mis à jour', 'success');
      } else {
        await authService.createUser(data);
        addToast('Utilisateur créé', 'success');
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      addToast(err.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
      throw err;
    }
  };

  const getRoleLabel = (user) => {
    const roles = user?.roles;
    if (Array.isArray(roles)) {
      const first = roles[0];
      return typeof first === 'string' ? first : first?.name || '-';
    }
    return user?.role || '-';
  };

  const isActive = (user) => {
    if (typeof user.is_active !== 'undefined') return !!parseInt(user.is_active, 10);
    return user.active !== false;
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Gestion des utilisateurs</h1>
        <button type="button" className="btn btn-primary" onClick={handleAdd}>
          + Nouvel utilisateur
        </button>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom d'utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Actif</th>
                <th>Dernière connexion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email || '-'}</td>
                  <td>{getRoleLabel(user)}</td>
                  <td>{isActive(user) ? 'Oui' : 'Non'}</td>
                  <td>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString('fr-FR')
                      : '-'}
                  </td>
                  <td className="actions-cell">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEdit(user)}
                    >
                      Modifier
                    </button>
                    {user.id !== MAIN_ADMIN_ID && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteClick(user)}
                      >
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        size="md"
      >
        <UserForm
          user={editingUser}
          onSave={handleSave}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>

      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          otherEditors={users.filter(
            (u) => u.id !== deletingUser.id && u.id !== MAIN_ADMIN_ID
          )}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeletingUser(null)}
        />
      )}
    </div>
  );
}

function DeleteUserModal({ user, otherEditors, onConfirm, onClose }) {
  const [reassignTo, setReassignTo] = useState(
    otherEditors.length > 0 ? String(otherEditors[0].id) : ''
  );
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      await onConfirm(reassignTo ? parseInt(reassignTo, 10) : null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-dialog-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Supprimer «&nbsp;{user.username}&nbsp;»</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <p style={{ marginBottom: '1rem', color: 'var(--error)', fontWeight: 500 }}>
            Attention : cette action est irréversible. L'utilisateur sera supprimé
            définitivement et ses émissions seront réassignées.
          </p>
          {otherEditors.length > 0 ? (
            <div className="form-group">
              <label>Réassigner ses émissions à :</label>
              <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                {otherEditors.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
          ) : (
            <p className="form-hint">
              Aucun autre éditeur disponible — les émissions ne seront pas réassignées.
            </p>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-danger" disabled={deleting}>
              {deleting ? 'Suppression...' : 'Supprimer définitivement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserForm({ user, onSave, onCancel }) {
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(
    user?.roles?.[0]?.name ?? user?.role ?? 'viewer'
  );
  const isActiveVal = typeof user?.is_active !== 'undefined'
    ? !!parseInt(user.is_active, 10)
    : user?.active !== false;
  const [active, setActive] = useState(isActiveVal);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        username,
        email,
        role,
        is_active: active ? 1 : 0,
      };
      if (password) {
        data.password = password;
      }
      await onSave(data);
    } catch {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Nom d'utilisateur *</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={!!user}
        />
      </div>
      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Mot de passe {user ? '(laisser vide pour ne pas modifier)' : '*'}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required={!user}
          minLength={6}
        />
      </div>
      <div className="form-group">
        <label>Rôle</label>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Actif
        </label>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
